import React, { useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import CSSModule from 'react-css-modules';
import { useLocation } from 'react-router-dom';
import { Grid, Monitor, PlusSquare, Shield, Tag as TicketIcon, User } from 'react-feather';

import { AppWrapperHeading, Footer } from 'components';
import NavBar from 'containers/Dashboard/Components/NavBar';
import styles from './AppWrapper.module.scss';

const HEADER_HEIGHT = 80;
const NAV_COLLAPSE_KEY = 'cbv2_nav_collapsed';
const RAIL_COLLAPSED = 56;  // matches $rail-collapsed in AppWrapper.module.scss
const RAIL_EXPANDED = 220;  // matches $rail-expanded in AppWrapper.module.scss

// Duplicated from PrivateAdminRoute.js — no shared constants file exists yet.
const ADMIN_EMAILS = ['charles.anthony.cruz02@gmail.com', 'info@mycouponbook.ca'];

const PUBLIC_PATH_PREFIXES = [
  '/',
  '/login',
  '/signup',
  '/forgot-password',
  '/privacy-policy',
  '/dealer-agreement',
  '/terms-of-service',
];

function isPublicRoute(pathname) {
  if (pathname === '/') return true;
  return PUBLIC_PATH_PREFIXES.slice(1).some(p => pathname.startsWith(p));
}

const AppWrapper = ({
  children,
  contentCentered,
  showHeader,
  showHeadingRow,
  headingRowRender,
  headingText,
  bgLight,
  isSignUpFlowActive,
  business,
}) => {
  const { pathname } = useLocation();

  const canShowNav = !isPublicRoute(pathname) && !isSignUpFlowActive;
  const canShowRail = canShowNav;

  const railItems = useMemo(() => [
    { to: '/dashboard', label: 'Dashboard', Icon: Grid },
    ...(ADMIN_EMAILS.includes(business?.email ?? "") ? [{ to: '/admin', label: 'Admin Panel', Icon: Shield }] : []),
    { to: '/coupons', label: 'My Coupons', Icon: TicketIcon },
    { to: '/coupons/create', label: 'Coupon Builder', Icon: PlusSquare },
    { to: '/account', label: 'My Account', Icon: User },
    { to: '/membership-plans', label: 'Membership Plans', Icon: Monitor },
  ], [business?.email]);

  // Default: OPEN (expanded) => collapsed = false
  const initialCollapsed = useMemo(() => {
    try {
      const saved = localStorage.getItem(NAV_COLLAPSE_KEY);
      return saved === '1'; // 1 means collapsed
    } catch {
      return false;
    }
  }, []);

  const [railCollapsed, setRailCollapsed] = useState(initialCollapsed);

  // Persist
  useEffect(() => {
    try {
      localStorage.setItem(NAV_COLLAPSE_KEY, railCollapsed ? '1' : '0');
    } catch {}
  }, [railCollapsed]);

  // Set CSS variable so layout never gets cut off by the sticky nav
  useEffect(() => {
    document.body.classList.add('cbv2-offset-header');
    document.body.style.setProperty('--cbv2-header-h', `${HEADER_HEIGHT}px`);

    const railW = canShowRail
      ? railCollapsed
        ? RAIL_COLLAPSED
        : RAIL_EXPANDED
      : 0;


    document.body.style.setProperty('--cbv2-rail-w', `${railW}px`);

    return () => {
      document.body.classList.remove('cbv2-offset-header');
      document.body.style.removeProperty('--cbv2-header-h');
    };
  }, [canShowRail, railCollapsed]);

  return (
    <div styleName={cx('app-container', { 'light-bg': bgLight })}>
      {contentCentered && !showHeader && <div className="mb-auto" />}
      {showHeader && canShowNav && <NavBar />}

      <main className={styles.mainContent}>
        <div className="container">
          <div className={cx('row mt-4', { 'justify-content-center': contentCentered })}>
            <div className="col-12">
              {showHeadingRow && (
                <div styleName="heading-row">
                  {headingRowRender ? (
                    headingRowRender()
                  ) : (
                    <AppWrapperHeading headingText={headingText} />
                  )}
                </div>
              )}

              <div
                className={cx('row', {
                  'justify-content-center': contentCentered,
                })}
              >
                {children}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer mtAuto />
    </div>
  );
};

AppWrapper.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.element,
  ]),
  bgLight: PropTypes.bool,
  contentCentered: PropTypes.bool,
  showHeader: PropTypes.bool,
  showHeadingRow: PropTypes.bool,
  headingRowRender: PropTypes.func,
  headingText: PropTypes.string,
  isSignUpFlowActive: PropTypes.bool,
};

export default CSSModule(AppWrapper, styles, { allowMultiple: true });